import { Link } from "react-router-dom";

import type { Spread } from "../bdl/model";
import { Card } from "../components/Card";
import { Figure } from "../components/Figure";
// Sample chains from the repo fixtures: the real format, frozen for the demo.
import rail from "../../../fixtures/spreads/welded-rail.json";
import alveoli from "../../../fixtures/spreads/lungs-surfactant.json";

const demo = rail as unknown as Spread;
const demo2 = alveoli as unknown as Spread;

/** The landing page. Same voice as the app: plain, no hype, no exclamation marks. */
export function Landing() {
  return (
    <div className="landing">
      <div className="hero">
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/favicon.svg" alt="" width={28} height={28} />
            <div className="head">BrainStorm</div>
          </div>
          <h1 style={{ marginTop: 12 }}>Deep topics, out of the pages they're buried in.</h1>
          <p className="lede">
            The best explanations of how things work sit in places nobody reads for fun: standards,
            lecture notes, patents, forty-year-old papers. BrainStorm takes one topic at a time and
            walks it as a chain. A question. Then one forced step after another, each with a
            diagram that moves, until the answer is the only thing left.
          </p>
          <p className="chrome" style={{ marginTop: 20 }}>On Android now, in testing. iOS later.</p>
        </div>
        <div style={{ justifySelf: "center" }}>
          <Card spread={demo} />
        </div>
      </div>

      <section>
        <div className="head">One format, whatever the source</div>
        <h2 style={{ marginTop: 8 }}>A small idea takes three steps. A deep one takes fifteen.</h2>
        <div className="arc">
          <div><h3>What you already believe</h3><p>Said in your words, and given its due: it was reasonable. Then the one fact that doesn't fit.</p></div>
          <div><h3>The obvious fix</h3><p>The one you'd try first, and why it fails, for a reason you can check.</p></div>
          <div><h3>The only door left</h3><p>The mechanism, a step at a time, built from things you already own.</p></div>
          <div><h3>So that's why</h3><p>The thing named last, as a consequence. <em>Welded rail has no gaps because it's clamped so it can't grow.</em></p></div>
        </div>
      </section>

      <section>
        <div className="head">Consistency is the product</div>
        <h2 style={{ marginTop: 8 }}>Colours mean roles, not topics.</h2>
        <p style={{ maxWidth: 620, marginTop: 12 }}>
          Every diagram in BrainStorm uses the same four colours for the same four things. A rail, a
          lung, a chip: after a dozen chains you read them without thinking. That's the point.
        </p>
        <div className="grammar">
          <div><div className="swatch" style={{ background: "var(--ink)" }} /><h3>Ink</h3><p>Structure. The mechanism's own parts, moving or not.</p></div>
          <div><div className="swatch" style={{ background: "var(--orange)" }} /><h3>Orange</h3><p>The driver. What applies force or energy.</p></div>
          <div><div className="swatch" style={{ background: "var(--blue)" }} /><h3>Blue</h3><p>The medium. What flows through: water, air, current, light.</p></div>
          <div><div className="swatch" style={{ background: "var(--green)" }} /><h3>Green</h3><p>The outcome. What's produced or acted on.</p></div>
        </div>
      </section>

      <section>
        <div className="head">Every step, one figure</div>
        <h2 style={{ marginTop: 8 }}>{demo2.card.title}</h2>
        <p className="hook" style={{ marginTop: 6 }}>{demo2.card.hook}</p>
        <div className="figgrid" style={{ marginTop: 24 }}>
          {demo2.chain.beats.map((b, i) => {
            const f = demo2.figures[b.figureId];
            return f && (
              <div className="cell" key={i}><Figure figure={f} size={220} /><div className="chrome" style={{ marginTop: 8 }}>{i + 1} · {b.head}</div></div>
            );
          })}
        </div>
        <p className="chrome" style={{ marginTop: 16, maxWidth: 620 }}>
          Every claim in a chain links to a source someone opened and checked.
        </p>
      </section>

      <div className="foot chrome">
        © {new Date().getFullYear()} Pessoa-Tech · <Link to="/privacy">privacy</Link> · <Link to="/admin">admin</Link>
      </div>
    </div>
  );
}
